import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { registerUser, loginUser, joinWaitlist } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_solaro-founding/artifacts/6pe0ovti_Solaro%20Capri%20Gin%20-%20Horizontal%20Vectorial%20Handpainted%20PNG%20TRANSPARENT.png";

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const res = await loginUser({ email, password });
        login(res.data.token, res.data.user);
        toast.success("Welcome back to Solaro");
        navigate("/grid");
      } else {
        if (honeypot) { setLoading(false); return; }
        if (!firstName.trim() || !lastName.trim()) {
          toast.error("Please enter your first and last name");
          setLoading(false);
          return;
        }
        const fullName = `${firstName.trim()} ${lastName.trim()}`;
        const res = await registerUser({ name: fullName, email, password, whatsapp: whatsapp || null });
        login(res.data.token, res.data.user);
        toast.success(`Bottle #${res.data.user.assigned_bottle} has been reserved for you!`);
        navigate("/grid");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Something went wrong";
      if (msg === "ALL_SOLD_OUT") {
        setShowWaitlist(true);
      } else {
        toast.error(msg);
      }
    }
    setLoading(false);
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (honeypot) { setLoading(false); return; }
      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      const res = await joinWaitlist({ name: fullName, email, whatsapp: whatsapp || null });
      setWaitlistDone(true);
      toast.success(`You're #${res.data.position} on the waitlist!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not join waitlist");
    }
    setLoading(false);
  };

  // Waitlist UI
  if (showWaitlist) {
    if (waitlistDone) {
      return (
        <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="waitlist-done">
          <div className="w-full max-w-md text-center animate-fade-in-up">
            <img src={LOGO_URL} alt="Solaro Capri Gin" className="h-14 sm:h-16 mx-auto mb-8 object-contain" />
            <h1 className="font-display text-2xl sm:text-3xl text-[#002366] mb-4 tracking-tight">
              You're on the List
            </h1>
            <p className="font-body text-sm text-[#1A2B4C]/60 max-w-sm mx-auto leading-relaxed mb-6">
              All bottles in this edition are currently reserved. You've been added to the waitlist and we'll notify you as soon as a spot opens up.
            </p>
            <div className="p-4 bg-[#002366]/5 border border-[#002366]/10 mb-6">
              <p className="font-body text-sm text-[#002366] italic">
                "Every bottle finds its person. We'll make sure one finds you too."
              </p>
              <p className="font-body text-xs text-[#002366]/50 mt-1">— Lorenzo</p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="waitlist-form">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Solaro Capri Gin" className="h-14 sm:h-16 mx-auto mb-8 object-contain" />
            <h1 className="font-display text-2xl sm:text-3xl text-[#002366] mb-3 tracking-tight">
              Edition Fully Reserved
            </h1>
            <p className="font-body text-sm text-[#1A2B4C]/60 max-w-sm mx-auto leading-relaxed">
              All bottles in this edition are spoken for. Join the waitlist and we'll notify you the moment a spot opens up.
            </p>
          </div>
          <form onSubmit={handleWaitlistSubmit} className="bg-white p-8 shadow-lg space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">First Name</Label>
                <Input data-testid="waitlist-firstname-input" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" required className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">Last Name</Label>
                <Input data-testid="waitlist-lastname-input" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" required className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white" />
              </div>
            </div>
            <div className="absolute opacity-0 top-0 left-0 h-0 w-0 -z-10" aria-hidden="true">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">Email</Label>
              <Input data-testid="waitlist-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">
                WhatsApp <span className="normal-case tracking-normal text-[#1A2B4C]/30">(optional)</span>
              </Label>
              <Input data-testid="waitlist-whatsapp-input" type="text" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+39 123 456 7890" className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white" />
            </div>
            <Button data-testid="waitlist-submit-btn" type="submit" disabled={loading} className="w-full bg-[#002366] text-white hover:bg-[#001133] rounded-none uppercase tracking-widest text-sm px-8 py-6">
              {loading ? "Joining..." : "Join Waitlist"}
            </Button>
          </form>
          <div className="text-center mt-4">
            <button type="button" onClick={() => { setShowWaitlist(false); setIsLogin(true); }} className="text-sm text-[#002366] hover:text-[#D4AF37] font-body transition-colors underline underline-offset-4">
              Already registered? Sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="auth-page">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="text-center mb-10">
          <img
            src={LOGO_URL}
            alt="Solaro Capri Gin"
            className="h-14 sm:h-16 mx-auto mb-8 object-contain"
            data-testid="auth-logo"
          />
          <h1 className="font-display text-3xl sm:text-4xl text-[#002366] mb-3 tracking-tight">
            {isLogin ? "Welcome Back" : "Join the Circolo Solaro"}
          </h1>
          <p className="font-body text-sm text-[#1A2B4C]/60 tracking-wide max-w-sm mx-auto">
            {isLogin
              ? "Sign in to view your reserved bottles"
              : "Sign up to instantly receive your limited edition bottle number."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white p-8 shadow-lg space-y-5">
          {!isLogin && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">
                    First Name
                  </Label>
                  <Input
                    data-testid="auth-firstname-input"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    required
                    className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">
                    Last Name
                  </Label>
                  <Input
                    data-testid="auth-lastname-input"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                    required
                    className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white"
                  />
                </div>
              </div>
              {/* Honeypot anti-spam field */}
              <div className="absolute opacity-0 top-0 left-0 h-0 w-0 -z-10" aria-hidden="true">
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>
            </>
          )}

          <div>
            <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">
              Email
            </Label>
            <Input
              data-testid="auth-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">
              Password
            </Label>
            <Input
              data-testid="auth-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
              required
              minLength={6}
              className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white"
            />
          </div>

          {!isLogin && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#1A2B4C]/50 font-body mb-2 block">
                WhatsApp <span className="normal-case tracking-normal text-[#1A2B4C]/30">(optional)</span>
              </Label>
              <Input
                data-testid="auth-whatsapp-input"
                type="text"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder="+39 123 456 7890"
                className="border-[#D1CFC7] focus:border-[#002366] focus:ring-[#D4AF37] bg-white"
              />
            </div>
          )}

          <Button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-[#002366] text-white hover:bg-[#001133] rounded-none uppercase tracking-widest text-sm px-8 py-6 transition-all duration-300 mt-2"
          >
            {loading ? "Please wait..." : isLogin ? "Sign In" : "Join the Circolo"}
          </Button>

          <div className="text-center pt-2">
            <button
              type="button"
              data-testid="auth-toggle-btn"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-[#002366] hover:text-[#D4AF37] font-body transition-colors underline underline-offset-4"
            >
              {isLogin ? "New here? Reserve your bottle" : "Already registered? Sign in"}
            </button>
          </div>
        </form>

        <p className="text-xs text-center text-[#1A2B4C]/40 mt-6 font-body leading-relaxed px-4">
          Your information is only used by Solaro and not shared with anyone.
        </p>
      </div>
    </div>
  );
}
